import { Component } from "react";
import { isChunkLoadError, refreshAppCaches, reserveAutoReload } from "@/lib/chunkRecovery";

/**
 * Route-level error boundary (https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary).
 *
 * - Only genuine chunk-load failures (stale deploy) trigger an automatic
 *   reload, and at most once per tab per build (see lib/chunkRecovery.js).
 *   Code defects such as ReferenceError/TypeError never reload: reloading
 *   cannot fix them and previously caused infinite reload loops.
 * - Every terminal state is a visible card with a manual Reload button.
 *   Copy is generic; error details go to the console only
 *   (WCAG 2.2 SC 3.3.1 Error Identification, SC 4.1.3 Status Messages).
 * - The service worker is never unregistered.
 *
 * There is no app-wide unsaved-form signal yet, so the single automatic
 * reload cannot check for dirty forms; it only fires for chunk errors, where
 * the page that failed to load cannot hold unsaved input.
 */
export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, recovering: false };
    this.reloadRequested = false;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[RouteErrorBoundary]", error, info?.componentStack || "");
    if (isChunkLoadError(error) && reserveAutoReload()) {
      this.setState({ recovering: true });
      refreshAppCaches().finally(this.reload);
    }
  }

  reload = () => {
    if (this.reloadRequested) return;
    this.reloadRequested = true;
    window.location.reload();
  };

  handleReload = () => {
    if (isChunkLoadError(this.state.error)) {
      refreshAppCaches().finally(this.reload);
      return;
    }
    this.reload();
  };

  render() {
    const { error, recovering } = this.state;
    if (!error) return this.props.children;

    if (recovering) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6">
          <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
            Updating to the latest version…
          </p>
        </div>
      );
    }

    const isChunkError = isChunkLoadError(error);

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div role="alert" className="max-w-md text-center space-y-4" data-testid="route-error-card">
          <h1 className="text-xl font-semibold text-foreground">
            {isChunkError ? "This page needs a refresh" : "This page couldn't be shown"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isChunkError
              ? "Part of the app didn't load, possibly because a newer version is available. Reload to continue."
              : "Something went wrong while showing this page. Reload to try again, or go back to the dashboard."}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Reload
            </button>
            <a
              href="/dashboard"
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Go to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
